/**
 * ケースの種類判定ヘルパー.
 *
 * 「⚠️ 利用上限到達」は AI 分析利用上限 (Free: 10/月 等) を超えたときに作られる
 * プレースホルダーで、原文 (body) は通常通り保存されるが分析されない。
 * 通常のヒヤリハットケースと同じ表に並ぶと管理者の運用ノイズになるため、
 * UI レイヤで明示的に分離する。
 *
 * 判定はタイトル文字列で行う (services/aiService.ts:255 で生成された定数).
 * 将来的に DB に `is_limit_exceeded` カラムを追加するときは、まずこのヘルパーの
 * 戻り値を切り替えるだけで全箇所が追従するように設計している.
 */

import type { Case } from '../types';

export const LIMIT_EXCEEDED_TITLE = '⚠️ 利用上限到達';

/** AI 上限到達によって作られたプレースホルダーケースか */
export const isLimitExceededCase = (c: Pick<Case, 'title'>): boolean => {
  return c.title === LIMIT_EXCEEDED_TITLE;
};

/* 上限到達ケースを除外した「実ヒヤリハット」のみを返す。
 *
 * 以前は <T extends Pick<Case,'title'>> のジェネリックだったが、推論が T=Case にならず
 * 戻り値が Pick<Case,'title'>[] に狭まっていた。その結果、呼び出し側で c.category1 等が
 * 「存在しないプロパティ」として型エラーになり、**実際のフィールド名の誤りが
 * 大量の既存エラーに埋もれて見えなくなっていた**（CSV出力が c.category を参照していて
 * 全行 undefined になっていた不具合がこれで見逃されていた）。
 * 呼び出し側は2箇所ともCase[]なので、素直にCase[]で受ける。 */
export const filterRealCases = (cases: Case[]): Case[] => {
  return cases.filter((c) => !isLimitExceededCase(c));
};

/** 上限到達ケースだけを返す */
export const filterLimitExceededCases = (cases: Case[]): Case[] => {
  return cases.filter((c) => isLimitExceededCase(c));
};

/**
 * 4M なぜなぜ分析の保存マーカー.
 * WhyAnalysisDemo の buildLessonText がこの文字列で始まるセクションを case.lesson に追記する.
 * このマーカーで「分析済みか」を判定し、表示時に lesson 本体と分離する.
 */
export const WHY_ANALYSIS_MARKER = '━━━ AI 4Mなぜなぜ分析';

/** このケースに 4M なぜなぜ分析が保存されているか */
export const hasWhyAnalysis = (lesson?: string | null): boolean =>
  !!lesson && lesson.includes(WHY_ANALYSIS_MARKER);

/**
 * lesson を「元の教訓 (base)」と「4M なぜなぜ分析 (why)」に分割する.
 * マーカーが無ければ why は null.
 */
export const splitLessonAndWhy = (
  lesson?: string | null
): { base: string; why: string | null } => {
  if (!lesson) return { base: '', why: null };
  const idx = lesson.indexOf(WHY_ANALYSIS_MARKER);
  if (idx === -1) return { base: lesson.trim(), why: null };
  return { base: lesson.slice(0, idx).trim(), why: lesson.slice(idx).trim() };
};

/* 教訓を「原言語版」と「日本語版」に割る。

   非日本語の報告では、教訓を1つのフィールドに
   「原言語版 → \n---\n【日本語訳】 → 日本語版」の順で併記している
   （supabase/functions/genbasafeai_analyze-report の ステップ2B-3）。
   1つの欄を投稿者と管理者で共有しているためで、そのまま出すと
   **管理者は外国語のブロックを読み飛ばして日本語まで下る**ことになる。

   読み手ごとに出し分けるためにここで割る。**データは変えない。**
   併記が無い（日本語の報告、または原言語版が畳まれた場合）なら
   native は null になり、呼び出し側は japanese だけを出せばよい。 */
export const splitLessonByLanguage = (
  lesson?: string | null,
): { native: string | null; japanese: string } => {
  if (!lesson) return { native: null, japanese: '' };
  const m = lesson.match(/\n*-{3,}\n*【日本語訳】\n*/);
  if (!m || m.index === undefined) return { native: null, japanese: lesson.trim() };
  const native = lesson.slice(0, m.index).trim();
  const japanese = lesson.slice(m.index + m[0].length).trim();
  /* 片方が空になる壊れ方をしていたら、割らずに全文を日本語側として返す。
     **表示から内容が消えるほうが、読みにくいより悪い。** */
  if (!native || !japanese) return { native: null, japanese: lesson.trim() };
  return { native, japanese };
};

// ---- 4M なぜなぜ分析テキストの構造化パース (WhyAnalysisDemo.buildLessonText と対) ----

export interface ParsedWhyPerspective {
  label: string; // 人 / 機械・設備 / 材料・環境 / 方法・管理
  whys: { question: string; answer: string }[];
  rootCause: string;
  immediate: { title: string; description: string };
  permanent: { title: string; description: string };
}

export interface ParsedWhyAnalysis {
  timestamp: string | null;
  perspectives: ParsedWhyPerspective[];
}

const splitTitleDesc = (raw: string): { title: string; description: string } => {
  const parts = raw.split(' — ');
  if (parts.length === 1) return { title: parts[0].trim(), description: '' };
  return { title: parts[0].trim(), description: parts.slice(1).join(' — ').trim() };
};

/**
 * 保存済み 4M なぜなぜテキストを構造化データに復元する.
 * フォーマットは buildLessonText:
 *   ━━━ AI 4Mなぜなぜ分析 (TS) ━━━
 *   【人】
 *     1. Q: ... / A: ...  (×5、A は複数行=原文併記の可能性あり)
 *     ▶ 根本原因: ...
 *     ▶ 即時対策: title — desc
 *     ▶ 恒久対策: title — desc
 * パースに失敗 (観点0件) なら null を返し、呼び出し側で raw 表示にフォールバックする.
 */
export const parseWhyAnalysis = (whyText?: string | null): ParsedWhyAnalysis | null => {
  if (!whyText || !whyText.includes(WHY_ANALYSIS_MARKER)) return null;

  const tsMatch = whyText.match(/AI 4Mなぜなぜ分析\s*\(([^)]+)\)/);
  const timestamp = tsMatch ? tsMatch[1] : null;

  // 【ラベル】 で観点チャンクに分割: [head, label1, content1, label2, content2, ...]
  const parts = whyText.split(/\n【([^】]+)】\n/);
  const perspectives: ParsedWhyPerspective[] = [];

  for (let i = 1; i < parts.length; i += 2) {
    const label = parts[i].trim();
    const content = parts[i + 1] ?? '';

    const rootIdx = content.indexOf('▶ 根本原因:');
    const whysPart = rootIdx >= 0 ? content.slice(0, rootIdx) : content;
    const restPart = rootIdx >= 0 ? content.slice(rootIdx) : '';

    // whys: "N. Q: ... A: ..." 形式. A は次の "N. Q:" 直前まで (複数行可).
    const whys: { question: string; answer: string }[] = [];
    const whyRe = /\d+\.\s*Q:\s*([\s\S]*?)\n\s*A:\s*([\s\S]*?)(?=\n\s*\d+\.\s*Q:|$)/g;
    let m: RegExpExecArray | null;
    while ((m = whyRe.exec(whysPart)) !== null) {
      whys.push({ question: m[1].trim(), answer: m[2].trim() });
    }

    const between = (from: string, to: string): string => {
      const s = restPart.indexOf(from);
      if (s < 0) return '';
      const start = s + from.length;
      const e = restPart.indexOf(to, start);
      return (e < 0 ? restPart.slice(start) : restPart.slice(start, e)).trim();
    };

    const rootCause = between('▶ 根本原因:', '▶ 即時対策:');
    const immRaw = between('▶ 即時対策:', '▶ 恒久対策:');
    const permRaw = (() => {
      const s = restPart.indexOf('▶ 恒久対策:');
      return s < 0 ? '' : restPart.slice(s + '▶ 恒久対策:'.length).trim();
    })();

    perspectives.push({
      label,
      whys,
      rootCause,
      immediate: splitTitleDesc(immRaw),
      permanent: splitTitleDesc(permRaw),
    });
  }

  if (perspectives.length === 0) return null;
  return { timestamp, perspectives };
};
